import React from "react";
import { useNavigate } from "react-router-dom";
import logo from "../Assets/fabhub-logo-nav.png";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaWhatsapp,
  FaLinkedinIn,
  FaPinterestP,
} from "react-icons/fa";
import { MdPhone, MdEmail, MdLocationOn } from "react-icons/md";
import useSiteContent from "../../utils/useSiteContent";
import { SOCIAL_LINKS_DEFAULTS } from "../../Constants/socialLinks";

// Footer social buttons — links, visibility and order are set in
// Admin → HomePage → Social Links
const SOCIAL_ICONS = {
  twitter: <FaTwitter size={14} />,
  facebook: <FaFacebookF size={14} />,
  instagram: <FaInstagram size={14} />,
  youtube: <FaYoutube size={14} />,
  linkedin: <FaLinkedinIn size={14} />,
  pinterest: <FaPinterestP size={14} />,
};

const Footer = () => {
  const navigate = useNavigate();
  const social = useSiteContent("social-links", SOCIAL_LINKS_DEFAULTS);

  const goToMyOrders = () => {
    navigate("/user-profile?tab=myOrders");
  };

  return (
    <footer id="site-footer" className="bg-[#0f0f13] text-white">
      {/* Purple gradient top border */}
      <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, var(--brand-primary-700), var(--brand-primary-500), var(--brand-primary-700))" }} />

      {/* Brand strip */}
      <div
        className="px-6 py-6 max-md:px-5 max-md:py-5 flex flex-col sm:flex-row items-center justify-between gap-4 max-md:gap-3"
        style={{ background: "linear-gradient(135deg, #1e0a3c 0%, #2d1060 100%)" }}
      >
        <div className="flex items-center gap-3">
          <div>
            <p className="text-white font-bold text-sm sm:text-base" style={{ fontFamily: "'Times New Roman', Serif" }}>
              AavryaA FabHub — Premium Indian Fabrics
            </p>
            <p className="text-[var(--brand-primary-300)] text-xs">U dream it, we customize it in perfect fit.</p>
          </div>
        </div>
        <a
          href="https://wa.me/919789389489?text=Hi%20I%20want%20to%20get%20in%20touch%20with%20AavryaA"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-5 py-2 rounded-full transition-all duration-300 shadow-lg"
        >
          <FaWhatsapp size={14} />
          Chat with Us
        </a>
      </div>

      {/* Main Grid */}
      <div className="max-w-7xl mx-auto px-6 py-10 max-md:px-5 max-md:py-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-md:gap-6">

        {/* Logo + Social */}
        <div className="flex flex-col gap-5">
          <img crossOrigin="anonymous" src={logo} alt="AavryaA Logo" className="w-40" />
          <p className="text-white text-xs leading-relaxed">
            Handcrafted fashion from Tamil Nadu. Bringing heritage fabric craftsmanship to every doorstep.
          </p>
          <div className="flex gap-3">
            {social.show
              .filter((name) => SOCIAL_ICONS[name] && social[name])
              .map((name) => ({ icon: SOCIAL_ICONS[name], href: social[name] }))
              .map(({ icon, href }, i) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 max-md:w-10 max-md:h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-[var(--brand-primary-600)] transition-all duration-300 text-gray-300 hover:text-white"
              >
                {icon}
              </a>
            ))}
          </div>
        </div>

        {/* Company */}
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-[var(--brand-primary-400)] mb-4 pb-2 border-b border-[var(--brand-primary-900)]">
            AavryaA Traders
          </h3>
          <ul className="space-y-2.5 max-md:space-y-1 text-sm text-white">
            <li>
              <Link to="/about-us" className="hover:text-[var(--brand-primary-300)] transition-colors max-md:inline-block max-md:py-1.5">About Us</Link>
            </li>
            <li>
              <a
                href="https://wa.me/919789389489?text=Hi%20I%20want%20to%20get%20in%20touch%20with%20AavryaA"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--brand-primary-300)] transition-colors max-md:inline-block max-md:py-1.5"
              >
                Get In Touch
              </a>
            </li>
            <li
              onClick={goToMyOrders}
              className="hover:text-[var(--brand-primary-300)] cursor-pointer transition-colors max-md:inline-block max-md:py-1.5"
            >
              My Orders
            </li>
            <li>
              <Link to="/reviews" className="hover:text-[var(--brand-primary-300)] transition-colors max-md:inline-block max-md:py-1.5">Reviews</Link>
            </li>
          </ul>
        </div>

        {/* More Information */}
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-[var(--brand-primary-400)] mb-4 pb-2 border-b border-[var(--brand-primary-900)]">
            More Information
          </h3>
          <ul className="space-y-2.5 max-md:space-y-1 text-sm text-white">
            <li>
              <Link to="/privacy-policy" className="hover:text-[var(--brand-primary-300)] transition-colors max-md:inline-block max-md:py-1.5">Privacy Policy</Link>
            </li>
            <li>
              <Link to="/refund-policy" className="hover:text-[var(--brand-primary-300)] transition-colors max-md:inline-block max-md:py-1.5">Refund & Return Policy</Link>
            </li>
            <li>
              <Link to="/shipping-policy" className="hover:text-[var(--brand-primary-300)] transition-colors max-md:inline-block max-md:py-1.5">Shipping & Payment Policy</Link>
            </li>
            <li>
              <Link to="/wash-care-instructions" className="hover:text-[var(--brand-primary-300)] transition-colors max-md:inline-block max-md:py-1.5">Wash Care Instructions</Link>
            </li>
            <li>
              <Link to="/terms-conditions" className="hover:text-[var(--brand-primary-300)] transition-colors max-md:inline-block max-md:py-1.5">Terms & Conditions</Link>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-[var(--brand-primary-400)] mb-4 pb-2 border-b border-[var(--brand-primary-900)]">
            Contact Us
          </h3>
          <ul className="space-y-3 max-md:space-y-2 text-sm text-white">
            <li>
              <a href="tel:+919789389489" className="flex items-start gap-3 hover:text-[var(--brand-primary-300)] transition-colors group max-md:py-1">
                <span className="mt-0.5 w-7 h-7 rounded-lg bg-[var(--brand-primary-900)]/60 flex items-center justify-center flex-shrink-0 group-hover:bg-[var(--brand-primary-700)] transition-colors">
                  <MdPhone size={14} className="text-[var(--brand-primary-300)]" />
                </span>
                +91 97893 89489
              </a>
            </li>
            <li>
              <a href="mailto:aavryaafabhub@gmail.com" className="flex items-start gap-3 hover:text-[var(--brand-primary-300)] transition-colors group max-md:py-1">
                <span className="mt-0.5 w-7 h-7 rounded-lg bg-[var(--brand-primary-900)]/60 flex items-center justify-center flex-shrink-0 group-hover:bg-[var(--brand-primary-700)] transition-colors">
                  <MdEmail size={14} className="text-[var(--brand-primary-300)]" />
                </span>
                aavryaafabhub@gmail.com
              </a>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 w-7 h-7 rounded-lg bg-[var(--brand-primary-900)]/60 flex items-center justify-center flex-shrink-0">
                <MdLocationOn size={14} className="text-[var(--brand-primary-300)]" />
              </span>
              <span>
                No 57, First Main Rd, Ramlinga Nagar, Woraiyur,<br />
                Tiruchirappalli, Tamil Nadu 620003
              </span>
            </li>
          </ul>
          <div className="mt-4 inline-flex items-center gap-2 bg-white/5 border border-[var(--brand-primary-900)] rounded-lg px-3 py-1.5">
      
            <span className="text-xs text-white">AavryaA - The HouZ Of GeeTaa</span>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="px-6 py-4 max-md:px-5 max-md:py-4 flex flex-col items-center justify-center gap-1 text-center" style={{ background: "#1a0a2e" }}>
        <p className="text-xs text-white/80">
          © {new Date().getFullYear()} All Rights Reserved | AavryaaFabHub
        </p>
        <p className="text-xs text-white/60">
AavryaA - The HouZ Of GeeTaa        </p>
      </div>
    </footer>
  );
};

export default Footer;
